/**
 * Company Settings Store - Zustand
 *
 * Centralized state management for company settings (self-service)
 *
 * Features:
 * - Fetch company profile
 * - Update company profile
 * - Loading and error states
 * - Toast notifications
 *
 * Backend API: /api/company-settings
 *
 * @module useCompanySettingsStore
 */

import { create } from 'zustand'
import { companySettingsService } from '@services/companySettingsService'
import toast from 'react-hot-toast'

/**
 * Company Settings Store
 *
 * State Management for Company Settings (Self-Service)
 */
export const useCompanySettingsStore = create((set, get) => ({
	// ============================================================================
	// STATE
	// ============================================================================

	/**
	 * Current company data
	 * @type {Object|null}
	 */
	company: null,

	/**
	 * Loading state for fetching data
	 * @type {boolean}
	 */
	loading: false,

	/**
	 * Saving state for update operations
	 * @type {boolean}
	 */
	saving: false,

	/**
	 * Error message if operation fails
	 * @type {string|null}
	 */
	error: null,

	/**
	 * Flag to track if data has been fetched
	 * @type {boolean}
	 */
	initialized: false,

	// ============================================================================
	// ACTIONS
	// ============================================================================

	/**
	 * Fetch company settings
	 * @returns {Promise<Object|null>} Company data or null on error
	 */
	fetchCompany: async () => {
		set({ loading: true, error: null })

		try {
			const response = await companySettingsService.getSettings()

			if (response.status === 200 && response.data) {
				set({
					company: response.data,
					loading: false,
					initialized: true,
				})
				return response.data
			} else {
				throw new Error(response.message || 'Failed to fetch company settings')
			}
		} catch (error) {
			const errorMessage = error.message || 'Failed to fetch company settings'
			set({
				error: errorMessage,
				loading: false,
				initialized: true,
			})
			toast.error(errorMessage)
			return null
		}
	},

	/**
	 * Update company profile
	 * @param {Object} data - Company data to update
	 * @returns {Promise<Object|null>} Updated company data or null on error
	 */
	updateCompany: async (data) => {
		set({ saving: true, error: null })

		try {
			const response = await companySettingsService.updateSettings(data)

			if (response.status === 200 && response.data) {
				set({
					company: response.data,
					saving: false,
				})
				toast.success('Company profile updated successfully')
				return response.data
			} else {
				throw new Error(response.message || 'Failed to update company profile')
			}
		} catch (error) {
			const errorMessage = error.message || 'Failed to update company profile'
			set({
				error: errorMessage,
				saving: false,
			})
			toast.error(errorMessage)
			return null
		}
	},

	/**
	 * Clear error state
	 */
	clearError: () => {
		set({ error: null })
	},

	/**
	 * Reset store to initial state
	 */
	reset: () => {
		set({
			company: null,
			loading: false,
			saving: false,
			error: null,
			initialized: false,
		})
	},
}))

export default useCompanySettingsStore
